import React, { useState, useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import CssBaseline from '@material-ui/core/CssBaseline';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import GenericDialogBox from '../../components/GenericDialogBox';
import InfoBox from '../../components/InfoBox';
import DataService from "../../services/data.service";
import { useTranslation } from "react-i18next";
import PermissionContext from '../../contexts/PermissionContext';


const validationSchema = yup.object().shape({
	id: yup
		.number(),
	username: yup
		.string('Enter the username')
		.required('Path is required')
		.min(4, 'Path should be of minimum 4 characters length'),
	can_list: yup
		.boolean(),
	can_get: yup
		.boolean(),
	can_post: yup
		.boolean(),
	can_patch: yup
		.boolean(),
	can_delete: yup
		.boolean(),
	user_id: yup
		.string('Enter user name')
		.required('User required')
});


const useStyles = makeStyles((theme) => ({
	paper: {
		marginTop: theme.spacing(18),
		maxWidth: 800,
		margin: 'auto',
		overflow: 'hidden',
		backgroundColor: 'white'
	},
	avatar: {
		margin: theme.spacing(1),
		backgroundColor: theme.palette.secondary.main,
	},
	form: {

		margin: theme.spacing(3, 2, 2),
	},
	submit: {
		margin: theme.spacing(3, 0, 2),
	},
}));



const endpoint = `user`


const UserForm = React.forwardRef((props, ref) => {
	const { t } = useTranslation();

	const { idToUpdate, permission } = props;

	const { userHasPermission } = useContext(PermissionContext)

	// console.log("UHP",userHasPermission)

	let hasPerm = userHasPermission(permission, 'can_list')
	let hasAddPerm = userHasPermission(permission, 'can_post')
	let hasEditPerm = userHasPermission(permission, 'can_patch')
	let hasGetPerm = userHasPermission(permission, 'can_get')
	let hasDeletePerm = userHasPermission(permission, 'can_delete')
	// for detail form    
	let hasDeleteDetailPerm = userHasPermission(`${permission}-detail`, 'can_delete')



	const [userName, setUserName] = React.useState("");
	const [firstName, setFirstName] = React.useState("");
	const [lastName, setLastName] = React.useState("");
	const [email, setEmail] = React.useState("");
	const [password, setPassword] = React.useState("");
	const [confirmPassword, setConfirmPassword] = React.useState("");

	const [isActive, setIsActive] = useState(true);
	const handleIsActive = (event) => {
		setIsActive(event.target.checked);
	};

	const [isStaff, setIsStaff] = useState(false);
	const handleIsStaff = (event) => {
		setIsStaff(event.target.checked);
	};

	const [isSuperUser, setIsSuperUser] = useState(false);
	const handleIsSuperUser = (event) => {
		setIsSuperUser(event.target.checked);
	};



	// INFO NOTIFICATION VARS
	const [infoOpen, setInfoOpen] = useState(false);
	const [infoTitle, setInfoTitle] = useState('');
	const [infoBody, setInfoBody] = useState('');
	//***********************

	//Dialog Vars
	const [dialogOpen, setDialogOpen] = useState(false);
	const [dialogueTitle, setDialogueTitle] = useState('');
	const [dialogueBody, setDialogueBody] = useState('');
	//**********

	// DIALOG FUNCTIONS 
	const handleDialogOk = () => {
		setDialogOpen(false)
	};

	const handleDialogClose = () => {
		setDialogOpen(false)
	};

	const openDialogBox = (title, body) => {
		setDialogueTitle(title)
		setDialogueBody(body)
		setDialogOpen(true)
	};
	//*******************

	// Notification INFO functions
	const openNoticeBox = (title, body) => {
		setInfoTitle(title)
		setInfoBody(body)
		setInfoOpen(true)
	};

	const handleInfoClose = () => {
		setInfoOpen(false)
	};
	// *****************

	const emptyData = {
		'id': typeof idToUpdate === 'object' ? 0 : idToUpdate,
		'username': userName,
		'first_name': firstName,
		'last_name': lastName,
		'email': email,
		'is_active': isActive,
		'is_staff': isStaff,
		'is_superuser': isSuperUser,
		'password': password,
		'confirm_password': confirmPassword
	}

	const [data, setData] = useState(emptyData);
	useEffect(() => {
		let id = typeof idToUpdate === 'object' ? 0 : idToUpdate
		getData(id)
	}, []);

	const getData = (id) => {
		DataService.get(id, endpoint)
			.then(response => {
				// console.log("data get", response.data)
				setData(response.data)
				setUserName(response.data.username)
				setFirstName(response.data.first_name)
				setLastName(response.data.last_name)
				setEmail(response.data.email)
				setPassword("")
				setIsActive(response.data.is_active == 1 ? true : false)
				setIsStaff(response.data.is_staff == 1 ? true : false)
				setIsSuperUser(response.data.is_superuser == 1 ? true : false)				
			})
			.catch(e => {
				if (e.response.status != '404') {
					openNoticeBox("Error", `Code: ${e.response.status} Message: ${e.response.statusText} - ${e.response.data.detail}`)
					// setData(emptyData)
				} else {
					setData(emptyData)
				}

			});
	}

	const updateData = (values) => {
		// values.user_id = userValue.id
		DataService.update(endpoint, values)
			.then(response => {
				openNoticeBox("Notice", t("record_updated_successfully", { "table": t("permission_table_title") }))
				setData(emptyData)
			})
			.catch(e => {
				openNoticeBox("Error", `Code: ${e.response.status} Message: ${e.response.statusText} - ${e.response.data.detail}`)
			});
	}

	const addData = (values) => {
		// values.user_id = userValue.id
		// console.log("values", values)
		DataService.create(endpoint, values)
			.then(response => {
				openNoticeBox("Notice", t("record_created_successfully", { "table": t("permission_table_title") }))
				setData(emptyData)
			})
			.catch(e => {
				openNoticeBox("Error", `Code: ${e.response.status} Message: ${e.response.statusText} - ${e.response.data.detail}`)
			});
	}


	const handleReset = (event) => {

	}

	const submitForm = (event) => {
		event.preventDefault();
		if (typeof userName === 'undefined' ) {
			openNoticeBox("Notice", t("fill_all_fields_msg", { "table": t("client_document_table_title") }))
			return false
		}
		if (password !== confirmPassword) {
			openNoticeBox("Notice", t("passwords_must_be_equal_msg", { "table": t("client_document_table_title") }))
			return false
		}
		
		let values = {
			'username': userName,
			'first_name': firstName,
			'last_name': lastName,
			'email': email,
			'is_active': isActive ? 1 : 0,
			'is_staff': isStaff ? 1 : 0,
			'is_superuser': isSuperUser ? 1 : 0,
			'password': password,
		}

		if (typeof idToUpdate === 'object') {
			addData(values)
		}

		if (idToUpdate > 0) {
			values.id = idToUpdate
			if (password === "") delete values.password		//if not password touching, no changes to it
			// delete values.username
			// console.log(values)
			updateData(values)
		}
		// console.log("submit ", values, idToUpdate)

	}

	const classes = useStyles();
	return (
		<Paper className={classes.paper}>
			<CssBaseline />
			<GenericDialogBox
				opn={dialogOpen}
				handleDialogOk={handleDialogOk}
				handleDialogClose={handleDialogClose}
				title={dialogueTitle}
				body={dialogueBody} />
			<InfoBox
				opn={infoOpen}
				handleDialogClose={handleInfoClose}
				title={infoTitle}
				body={infoBody} />

			<form className={classes.form} onSubmit={submitForm} onReset={handleReset}>
				<Grid container spacing={1}>
					<Grid item xs={12}>
						<TextField
							variant="outlined"
							margin="normal"
							fullWidth
							id="username"
							label={t("user_form_lbl_username")}
							name="username"
							value={userName}
							onChange={event => setUserName(event.target.value)}
						/>
					</Grid>
					<Grid item xs={6}>
						<TextField
							variant="outlined"
							margin="normal"
							fullWidth
							id="first_name"
							label={t("user_form_lbl_first_name")}
							name="first_name"
							value={firstName}
							onChange={event => setFirstName(event.target.value)}
						/>
					</Grid>
					<Grid item xs={6}>
						<TextField
							variant="outlined"
							margin="normal"
							fullWidth
							id="last_name"
							label={t("user_form_lbl_last_name")}
							name="last_name"
							value={lastName}
							onChange={event => setLastName(event.target.value)}
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							variant="outlined"
							margin="normal"
							fullWidth
							id="email"
							label={t("user_form_lbl_email")}
							name="email"
							value={email}
							onChange={event => setEmail(event.target.value)}
						/>
					</Grid>
					<Grid item xs={6}>
						<TextField
							variant="outlined"
							margin="normal"
							type="password"
							fullWidth
							id="password"
							label={t("user_form_lbl_password")}
							name="password"
							value={password}
							onChange={event => setPassword(event.target.value)}
						/>
					</Grid>
					<Grid item xs={6}>
						<TextField
							variant="outlined"
							margin="normal"
							fullWidth
							type="password"
							id="confirm_password"
							label={t("user_form_lbl_confirm_password")}
							name="confirm_password"
							value={confirmPassword}
							onChange={event => setConfirmPassword(event.target.value)}
						/>
					</Grid>
					<Grid item xs={4}>
						<FormControlLabel
							value={isActive}
							checked={isActive}
							control={<Checkbox color="primary" />}
							label={t("user_form_lbl_invoice_is_active")}
							labelPlacement="end"
							onChange={handleIsActive}
							name="is_active"
							id="is_active"
						// disabled={idToUpdate === 0 ? false: true}
						/>
					</Grid>
					<Grid item xs={4}>
						<FormControlLabel
							value={isStaff}
							checked={isStaff}
							control={<Checkbox color="primary" />}
							label={t("user_form_lbl_invoice_is_staff")}
							labelPlacement="end"
							onChange={handleIsStaff}
							name="is_staff"
							id="is_staff"
						// disabled={idToUpdate === 0 ? false: true}
						/>
					</Grid>
					<Grid item xs={4}>
						<FormControlLabel
							value={isSuperUser}
							checked={isSuperUser}
							control={<Checkbox color="primary" />}
							label={t("user_form_lbl_invoice_is_superuser")}
							labelPlacement="end"
							onChange={handleIsSuperUser}
							name="is_superuser"
							id="is_superuser"
						// disabled={idToUpdate === 0 ? false: true}
						/>
					</Grid>

					<Grid item xs={6}>

					</Grid>
					<Grid item xs={12}>
						<Button
							type="submit"
							fullWidth
							variant="contained"
							color="primary"
							className={classes.submit}
							disabled={typeof idToUpdate === 'object' && hasAddPerm ? false : hasEditPerm ? false : true}
						>
							{typeof idToUpdate === 'object' ? t("save_form_button") : t("update_form_button")}
						</Button>
					</Grid>

				</Grid>
			</form>

			<Box mt={8}>

			</Box>
		</Paper>
	)
})

export default UserForm;
